/**
 * @file CustomerConsentsSection.tsx
 * @description «Согласия (ФЗ-152)» в профиле клиента (phase08.5; phase19.16,
 * партия «Юридические документы», шаг Б — uiux-legal-steps-b-c §3): по
 * каждому виду — последняя запись с редакцией документа и «История (N)».
 *
 * Отзыв — окном кабинета `ConfirmDialog` вместо системного `confirm()`
 * (TD-487); записи из ЛК отзывает только сам клиент. После отзыва список
 * перечитывается с сервера. Состояния: загрузка — две строки-заглушки,
 * пусто — когда согласия записываются, ошибка — «Повторить».
 * Якорь `#consents` — переход из карточки заказа («Принятые условия»).
 */

import { useCallback, useEffect, useState } from "react";

import { ConsentKindItem } from "./ConsentKindItem";
import { ConfirmDialog } from "../../../../components/ui/ConfirmDialog";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { SectionLabel } from "../../../../components/ui/SectionLabel";
import { useConfirm } from "../../../../components/ui/use-confirm";
import { useHashScroll } from "../../../../hooks/useHashScroll";
import { isAbsoluteHref, useShopSiteUrl } from "../../../../hooks/useShopSiteUrl";
import { ApiError } from "../../../../lib/api-client";
import { consents, type ConsentKindGroupDto, type ConsentRecordDto } from "../../../../lib/api-customers";
import { CONSENT_KIND_LABEL } from "../../../../lib/customer-consents";

interface Props {
  customerId: string;
  canManage: boolean;
}

/** Текст окна отзыва (§3.1). */
function revokeRequest(record: ConsentRecordDto) {
  return {
    title: `Отозвать согласие «${CONSENT_KIND_LABEL[record.consentType]}»?`,
    description: "Запись о согласии останется в истории с отметкой об отзыве. Клиенту перестанут приходить письма, требующие этого согласия.",
    action: "Отозвать",
    danger: true,
  };
}

export function CustomerConsentsSection({ customerId, canManage }: Props) {
  const notify = useNotify();
  const { confirm, request, settle } = useConfirm();
  const [groups, setGroups] = useState<ConsentKindGroupDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Сервер отдаёт абсолютные адреса редакций; адрес сайта — запас для относительных.
  const relative = (groups ?? []).some((g) => [g.current, ...g.history].some((r) => r.legalRevision && !isAbsoluteHref(r.legalRevision.href)));
  const siteUrl = useShopSiteUrl(relative);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setGroups(await consents.listGrouped(customerId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить согласия.");
    }
  }, [customerId]);

  useEffect(() => {
    setGroups(null);
    void refresh();
  }, [refresh]);

  // Переход «…→ Согласия» из заказа: прокрутить к разделу, когда он загрузился.
  useHashScroll(groups !== null);

  async function onRevoke(record: ConsentRecordDto) {
    if (!(await confirm(revokeRequest(record)))) return;
    try {
      await consents.revoke(record.id);
      notify.success("Согласие отозвано");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось отозвать согласие. Попробуйте ещё раз.");
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div id="consents" className="scroll-mt-20">
          <SectionLabel as="h2" className="mb-1">
            Согласия (ФЗ-152)
          </SectionLabel>
        </div>
        {error ? (
          <ListError message="Не удалось загрузить согласия." onRetry={() => void refresh()} />
        ) : groups === null ? (
          <ListSkeleton rows={2} compact />
        ) : groups.length === 0 ? (
          <p className="type-body text-on-surface-variant">
            Согласий нет. Они записываются, когда клиент оформляет заказ, регистрируется или меняет настройки в личном кабинете.
          </p>
        ) : (
          <div className="flex flex-col divide-y divide-[var(--border)]">
            {groups.map((group) => (
              <ConsentKindItem key={`${group.kind}:${group.current.origin ?? "CRM"}`} group={group} siteUrl={siteUrl} canRevoke={canManage} onRevoke={(r) => void onRevoke(r)} />
            ))}
          </div>
        )}
        <ConfirmDialog request={request} onSettle={settle} />
      </EditorialCard.Body>
    </EditorialCard>
  );
}
